/**
 * EquipmentEditor - Panel for editing selected HVAC equipment properties
 */

import React, { useState } from 'react';
import { FloatingPanel, PanelSection } from '../ui/Panel';
import { Button } from '../ui/Button';
import { Select, TextInput } from '../ui/Input';
import {
  EQUIPMENT_TYPES,
  EQUIPMENT_CATEGORIES,
} from '../../constants/hvacEquipment';

export default function EquipmentEditor({
  equipment,
  onUpdate,
  onDelete,
  onClose,
  x = 20,
  y = 100,
  isMobile = false,
}) {
  const [minimized, setMinimized] = useState(isMobile);

  if (!equipment) return null;

  const equipType = EQUIPMENT_TYPES[equipment.type] || EQUIPMENT_TYPES['furnace-gas'];
  const sizes = equipType.sizes || [];

  const sizeOptions = sizes.map((size) => ({
    value: size.value,
    label: size.label,
  }));

  const rotationPresets = [0, 90, 180, 270];

  return (
    <FloatingPanel
      title="Equipment Properties"
      onClose={onClose}
      onMinimize={() => setMinimized(!minimized)}
      minimized={minimized}
      isMobile={isMobile}
      x={x}
      y={y}
      maxHeight={500}
    >
      <div style={{ padding: '12px' }}>
        {/* Equipment Type Info */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px',
            padding: '8px',
            background: `${equipType.color}22`,
            borderRadius: '6px',
            border: `1px solid ${equipType.color}44`,
          }}
        >
          <div
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '4px',
              background: equipType.color,
            }}
          />
          <div>
            <div style={{ fontWeight: '600', color: '#fff' }}>
              {equipType.label}
            </div>
            <div style={{ fontSize: '11px', color: '#8090a0' }}>
              {sizes.find(s => s.value === equipment.size)?.label || ''}
            </div>
          </div>
        </div>

        <PanelSection title="Type">
          <Select
            label="Equipment Type"
            value={equipment.type}
            onChange={(type) => {
              const newEquipType = EQUIPMENT_TYPES[type];
              onUpdate({ type, size: newEquipType.defaultSize });
            }}
            options={Object.entries(EQUIPMENT_CATEGORIES).flatMap(([catKey, category]) =>
              category.types.map((typeKey) => {
                const type = EQUIPMENT_TYPES[typeKey];
                if (!type) return null;
                return { value: typeKey, label: `${category.label}: ${type.label}` };
              }).filter(Boolean)
            )}
          />
        </PanelSection>

        {/* Size/Capacity */}
        {sizes.length > 1 && (
          <PanelSection title="Size/Capacity">
            <Select
              label="Equipment Size"
              value={equipment.size}
              onChange={(size) => onUpdate({ size })}
              options={sizeOptions}
            />
          </PanelSection>
        )}

        <PanelSection title="Rotation">
          <div style={{ display: 'flex', gap: '4px' }}>
            {rotationPresets.map((deg) => (
              <Button
                key={deg}
                variant={(equipment.rotation || 0) === deg ? 'primary' : 'secondary'}
                size="small"
                onClick={() => onUpdate({ rotation: deg })}
                style={{ flex: 1, fontSize: '11px' }}
              >
                {deg}°
              </Button>
            ))}
          </div>
        </PanelSection>

        <PanelSection title="Model Info">
          <TextInput
            label="Model Number"
            value={equipment.modelNumber || ''}
            onChange={(modelNumber) => onUpdate({ modelNumber })}
            placeholder="e.g., XC95M080"
          />
        </PanelSection>

        <PanelSection title="Notes">
          <textarea
            value={equipment.notes || ''}
            onChange={(e) => onUpdate({ notes: e.target.value })}
            placeholder="Add notes..."
            rows={2}
            style={{
              width: '100%',
              padding: '8px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '4px',
              color: '#fff',
              fontSize: '12px',
              resize: 'vertical',
            }}
          />
        </PanelSection>

        <PanelSection title="Actions">
          <Button
            variant="danger"
            onClick={onDelete}
            style={{ width: '100%' }}
          >
            Delete Equipment
          </Button>
        </PanelSection>
      </div>
    </FloatingPanel>
  );
}
